import type { HomeFinancial, HomeWeekDay, LowStockItem } from '../types/dashboard';

// Ola 2: lógica de presentación PURA del Inicio (sin React) — testeable en node.
// Regla del plan: nada de cifras del piloto; todo se deriva del resumen del negocio actual.

export interface BreakEvenHeadline {
  tone: 'ok' | 'warn' | 'neutral';
  text: string;
}

const money = (value: string | number) => `$${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Frase del semáforo del día. Sin configuración => estado NEUTRAL que invita a configurar (nunca
 * rojo: falta información, no hay nada "mal"). Con meta => arriba/abajo/alcanzada en lenguaje
 * cotidiano, siempre citando la meta para dar contexto.
 */
export function breakEvenHeadline(financial: Pick<HomeFinancial, 'breakEvenStatus' | 'breakEvenDifference' | 'breakEven'>): BreakEvenHeadline {
  if (financial.breakEvenStatus === 'NOT_CONFIGURED' || !financial.breakEven.configured) {
    return { tone: 'neutral', text: 'Configura tus gastos para calcular tu punto de equilibrio.' };
  }
  const target = money(financial.breakEven.dailyTarget);
  const difference = Math.abs(Number(financial.breakEvenDifference ?? 0));
  if (financial.breakEvenStatus === 'MET') return { tone: 'ok', text: `Justo en tu punto de equilibrio (${target}).` };
  if (financial.breakEvenStatus === 'ABOVE') return { tone: 'ok', text: `${money(difference)} arriba de tu punto de equilibrio (${target}).` };
  return { tone: 'warn', text: `${money(difference)} abajo de tu punto de equilibrio (${target}).` };
}

export interface LowStockRowView {
  label: string;
  tone: 'bad' | 'warn' | 'soon';
  action: 'purchase' | 'adjust';
  actionLabel: string;
}

/**
 * Cómo se lee cada renglón de "se está acabando". La urgencia visual se deriva de la propia
 * estimación (≤2 días = urgente; ≤7 días = pronto, una ventana de consumo), no de umbrales del
 * piloto. Negativo => problema de inventario con acción de ajuste, jamás "-N días".
 */
export function lowStockRowView(item: Pick<LowStockItem, 'state' | 'daysLeft' | 'estimateQuality'>): LowStockRowView {
  if (item.state === 'NEGATIVE_STOCK') {
    return { label: 'Existencia negativa — revisa el inventario', tone: 'bad', action: 'adjust', actionLabel: 'Ajustar inventario' };
  }
  if (item.state === 'OUT_OF_STOCK') {
    return { label: 'Agotado', tone: 'bad', action: 'purchase', actionLabel: 'Registrar compra' };
  }
  const days = item.daysLeft ?? 0;
  const daysText = days < 1 ? 'menos de 1 día' : days === 1 ? '~1 día' : `~${days} días`;
  const preliminary = item.estimateQuality === 'PRELIMINARY' ? ' · estimación preliminar' : '';
  return {
    label: `${daysText}${preliminary}`,
    tone: days <= 2 ? 'bad' : days <= 7 ? 'warn' : 'soon',
    action: 'purchase',
    actionLabel: 'Registrar compra',
  };
}

export interface SparklineGeometry {
  points: Array<{ x: number; y: number }>;
  path: string;
  targetY: number | null;
  max: number;
}

export const SPARK_WIDTH = 100;
export const SPARK_HEIGHT = 36;
const SPARK_PAD = 3;

/**
 * Geometría de la mini gráfica de 7 días (viewBox 100×36). La línea de la meta solo existe si hay
 * meta configurada — sin meta no se dibuja ninguna línea ficticia. Un día en $0 se dibuja en $0
 * (día sin ventas registradas: honesto, no un hueco).
 */
export function sparklineGeometry(week: HomeWeekDay[], dailyTarget: string | null): SparklineGeometry {
  const totals = week.map((day) => Number(day.total));
  const target = dailyTarget === null ? null : Number(dailyTarget);
  const max = Math.max(1, ...totals, target ?? 0);
  const usableHeight = SPARK_HEIGHT - SPARK_PAD * 2;
  const stepX = week.length > 1 ? SPARK_WIDTH / (week.length - 1) : SPARK_WIDTH;
  const toY = (value: number) => SPARK_HEIGHT - SPARK_PAD - (value / max) * usableHeight;
  const points = totals.map((value, index) => ({ x: Number((index * stepX).toFixed(2)), y: Number(toY(value).toFixed(2)) }));
  return {
    points,
    path: points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' '),
    targetY: target === null ? null : Number(toY(target).toFixed(2)),
    max,
  };
}

/** Etiqueta corta de un día de la semana para la gráfica (ej. "lun 24"), en español. */
export function weekDayLabel(dateIso: string): string {
  const [year, month, day] = dateIso.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 2026, (month ?? 1) - 1, day ?? 1));
  const weekday = new Intl.DateTimeFormat('es-MX', { weekday: 'short', timeZone: 'UTC' }).format(date);
  return `${weekday} ${day}`;
}
